/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useGetDasInventoryQuery } from "../../../store/api/devices/deviceApiSlice";

import {
  parseDeviceAttributes,
} from "../../../components/Drawers/DasDeviceDetails/utils";

import DetailRow from "../../../components/Drawers/DasDeviceDetails/components/DetailRow";
import AlarmTable from "../../../components/Drawers/DasDeviceDetails/components/AlarmTable";

import { AlarmDefinition } from "../../../components/Drawers/DasDeviceDetails/types";

import { resolveDeviceStatusLabel } from "../../../utils/deviceStatus";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";

const dcuAlarmDefinitions: AlarmDefinition[] = [
  { bit: 0, name: "Power Alarm" },
  { bit: 1, name: "Sync Alarm" },
  { bit: 2, name: "Network Alarm" },
  { bit: 3, name: "Temperature Alarm" },
  { bit: 4, name: "Hardware Alarm" },
  { bit: 5, name: "Configuration Alarm" },
  { bit: 6, name: "Communication Alarm" },
  { bit: 7, name: "Reserved" },
];

const toDisplayValue = (
  value: unknown
): string | number => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "N/A";
    }
  }

  return value as string | number;
};

const formatMetricValue = (
  value: unknown
): string => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }


  return String(num);
};

const DcuInfo: React.FC = () => {
  const location = useLocation();

  const selectedDevice = useSelector(
    selectedDeviceSelector
  );

  const deviceObjectKey =
    selectedDevice?.objectKey ||
    location.pathname.split("/")[2];

  const {
    data: dasInventory = [],
    isLoading,
  } = useGetDasInventoryQuery(
    { deviceKey: deviceObjectKey },
    {
      skip: !deviceObjectKey,
      pollingInterval: 30000,
    }
  );

  const dcuDevice = useMemo(() => {
    return dasInventory.find((device) => {
      const type = String(
        device?.equipmentType || ""
      ).toUpperCase();
      return (
        type === "DCU" || type === "SDCU"
      );
    });
  }, [dasInventory]);

  const parsedAttributes = useMemo(() => {
    if (!dcuDevice?.attributes) {
      return {};
    }

    return parseDeviceAttributes(
      dcuDevice.attributes
    );
  }, [dcuDevice]);

  const dcuAlarms =
    parsedAttributes.dcuAlarms ??
    parsedAttributes.deuAlarms ??
    parsedAttributes.alarms;

  const dcuAlarmsMask =
    parsedAttributes.dcuAlarmsMask ??
    parsedAttributes.deuAlarmsMask ??
    parsedAttributes.alarmsMask;

  const statusLabel = dcuDevice
    ? resolveDeviceStatusLabel(
        dcuDevice.status
      )
    : "Unknown";

  const isDisconnected =
    statusLabel === "Disconnected";

  const dcuCommInfo =
    parsedAttributes.dcuCommInfo || {};

  const dcuFanAlarms =
    parsedAttributes.dcuFanAlram || [];

  const rfInfoSorted = useMemo(() => {
    const dcuRfInfo =
      parsedAttributes.dcuRfInfo || [];

    return [...dcuRfInfo].sort(
      (a, b) =>
        Number(a.dcuRfInfoIndex) -
        Number(b.dcuRfInfoIndex)
    );
  }, [parsedAttributes.dcuRfInfo]);

  const sfpInfoSorted = useMemo(() => {
    const dcuSfpInfo =
      parsedAttributes.dcuSFPInfo || [];

    return [...dcuSfpInfo].sort(
      (a, b) => {
        const aVal = a.dcuSFPPortNum;
        const bVal = b.dcuSFPPortNum;

        const aNum = Number(aVal);
        const bNum = Number(bVal);

        if (
          !Number.isNaN(aNum) &&
          !Number.isNaN(bNum)
        ) {
          return aNum - bNum;
        }

        return String(aVal).localeCompare(
          String(bVal)
        );
      }
    );
  }, [parsedAttributes.dcuSFPInfo]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <CircularProgress
          size="4rem"
          color="primary"
        />
      </div>
    );
  }

  if (!dcuDevice) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          No DCU device found in this DAS
          system.
        </p>
      </div>
    );
  }


  return (
    <div className="space-y-4">
      {/* ================= COMMUNICATION INFO ================= */}

      <Card className="shadow-sm border">
          <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">DCU Communication Info</p>
          </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-2">
            <DetailRow
              label="IP Type"
              value={toDisplayValue(
                dcuCommInfo.dcuIPType
              )}
            />

            <DetailRow
              label="IP Address"
              value={toDisplayValue(
                dcuCommInfo.dcuIPAddress
              )}
            />

            <DetailRow
              label="Default Gateway"
              value={toDisplayValue(
                dcuCommInfo.dcuDefaultGateway
              )}
            />

            <DetailRow
              label="Subnet Mask"
              value={toDisplayValue(
                dcuCommInfo.dcuSubnetMask
              )}
            />

            <DetailRow
              label="IPv6 Prefix Length"
              value={toDisplayValue(
                dcuCommInfo.dcuIPV6SubPreLen
              )}
            />

            <DetailRow
              label="SNMP Version"
              value={toDisplayValue(
                dcuCommInfo.snmpVersion
              )}
            />

            <DetailRow
              label="SNMP Trap Destination"
              value={toDisplayValue(
                dcuCommInfo.snmpTrapDestIP
              )}
            />

            <DetailRow
              label="SNMP Trap Port"
              value={toDisplayValue(
                dcuCommInfo.snmpTrapPort
              )}
            />

            <DetailRow
              label="SNMP Port"
              value={toDisplayValue(
                dcuCommInfo.snmpPort
              )}
            />

            <DetailRow
              label="Read Community"
              value={toDisplayValue(
                dcuCommInfo.readCommunity
              )}
            />

            <DetailRow
              label="Write Community"
              value={toDisplayValue(
                dcuCommInfo.writeCommunity
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* ================= FAN ALARMS ================= */}

      {dcuFanAlarms.length > 0 && (
        <Card className="shadow-sm border">
          <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Fan Alarms</p>
          </CardHeader>

          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {dcuFanAlarms.map(
                (
                  fan: any,
                  index: number
                ) => {
                  const isAlarmActive =
                    fan.dcuFanAlarm === "1";

                  return (
                    <div
                      key={index}
                      className="rounded-xl border p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">
                          Fan{" "}
                          {
                            fan.dcuFanNumber
                          }
                        </div>

                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor:
                              isAlarmActive
                                ? "#ef4444"
                                : "#22c55e",
                            boxShadow: `0 0 8px ${
                              isAlarmActive
                                ? "#ef4444"
                                : "#22c55e"
                            }`,
                          }}
                        />
                      </div>

                      <div className="mt-3 text-xs text-muted-foreground">
                        Status:{" "}
                        <span className="font-medium text-foreground">
                          {isAlarmActive
                            ? "Alarm Active"
                            : "Normal"}
                        </span>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================= RF INFORMATION ================= */}

{rfInfoSorted.length > 0 && (
  <Card className="shadow-sm border overflow-hidden">
      <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">RF Information</p>
      </CardHeader> 
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left px-4 py-3 font-semibold border-b min-w-[180px]">
                Parameters
              </th>

              {rfInfoSorted.map((rf: any) => (
                <th
                  key={rf.dcuRfInfoIndex}
                  className="text-left px-4 py-3 font-semibold border-b min-w-[140px]"
                >
                  Port {rf.dcuRfInfoIndex}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* DL POWER */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium  bg-muted/20">
                DL Power In
              </td>

              {rfInfoSorted.map((rf: any) => (
                <td
                  key={`dl-${rf.dcuRfInfoIndex}`}
                  className="px-4 py-3"
                >
                  {formatMetricValue(
                    rf.dcuDlPwrIn
                  )}{" "}
                  dBm
                </td>
              ))}
            </tr>

            {/* UL POWER */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium  bg-muted/20">
                UL Power Out
              </td>

              {rfInfoSorted.map((rf: any) => (
                <td
                  key={`ul-${rf.dcuRfInfoIndex}`}
                  className="px-4 py-3"
                >
                  {formatMetricValue(
                    rf.dcuUlPwrOut
                  )}{" "}
                  dBm
                </td>
              ))}
            </tr>

            {/* RF SWITCH */}
            <tr>
              <td className="px-4 py-3 font-medium bg-muted/20">
                RF Switch
              </td>

              {rfInfoSorted.map((rf: any) => {
                const rfEnabled =
                  rf.dcuRFSwitch === "1";

                return (
                  <td
                    key={`switch-${rf.dcuRfInfoIndex}`}
                    className="px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            rfEnabled
                              ? "#22c55e"
                              : "#ef4444",
                          boxShadow: `0 0 6px ${
                            rfEnabled
                              ? "#22c55e"
                              : "#ef4444"
                          }`,
                        }}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
)}
    {/* ================= SFP INFORMATION ================= */}

{sfpInfoSorted.length > 0 && (
  <Card className="shadow-sm border overflow-hidden">
    <CardHeader className="px-4 py-3">
        <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">SFP Information</p>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left px-4 py-3 font-semibold border-b  min-w-[180px]">
                Parameters
              </th>

              {sfpInfoSorted.map((sfp: any) => (
                <th
                  key={sfp.dcuSFPInfoIndex}
                  className="text-left px-4 py-3 font-semibold border-b min-w-[140px]"
                >
                  Port {sfp.dcuSFPPortNum}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* TEMPERATURE */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium  bg-muted/20">
                Temperature
              </td>

              {sfpInfoSorted.map((sfp: any) => (
                <td
                  key={`temp-${sfp.dcuSFPInfoIndex}`}
                  className="px-4 py-3"
                >
                  {formatMetricValue(
                    sfp.dcuSFPTemperature
                  )}{" "}
                  °C
                </td>
              ))}
            </tr>

            {/* TX POWER */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium bg-muted/20">
                TX Power
              </td>

              {sfpInfoSorted.map((sfp: any) => (
                <td
                  key={`tx-${sfp.dcuSFPInfoIndex}`}
                  className="px-4 py-3"
                >
                  {formatMetricValue(
                    sfp.dcuSFPTxPower
                  )}{" "}
                  dBm
                </td>
              ))}
            </tr>

            {/* RX POWER */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium  bg-muted/20">
                RX Power
              </td>

              {sfpInfoSorted.map((sfp: any) => (
                <td
                  key={`rx-${sfp.dcuSFPInfoIndex}`}
                  className="px-4 py-3"
                >
                  {formatMetricValue(
                    sfp.dcuSFPRxPower
                  )}{" "}
                  dBm
                </td>
              ))}
            </tr>

            {/* ALARM */}
            <tr className="border-b">
              <td className="px-4 py-3 font-medium  bg-muted/20">
                Alarm Status
              </td>

              {sfpInfoSorted.map((sfp: any) => {
                const hasAlarm =
                  Number(
                    sfp.dcuSFPAlarms
                  ) > 0;

                return (
                  <td
                    key={`alarm-${sfp.dcuSFPInfoIndex}`}
                    className="px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            hasAlarm
                              ? "#ef4444"
                              : "#22c55e",
                          boxShadow: `0 0 6px ${
                            hasAlarm
                              ? "#ef4444"
                              : "#22c55e"
                          }`,
                        }}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>

            
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
)}

      {/* ================= ALARMS ================= */}

      {(dcuAlarms !== undefined ||
        dcuAlarmsMask !== undefined) && (
        <Card className="shadow-sm border overflow-hidden">
          <CardHeader className="px-4 py-3">
             <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">DCU Alarms</p>
          </CardHeader>
          <CardContent className="p-2">
            <AlarmTable
              alarms={dcuAlarms}
              alarmsMask={dcuAlarmsMask}
              alarmDefinitions={
                dcuAlarmDefinitions
              }
              isDisconnected={
                isDisconnected
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DcuInfo;